import { BaseApiService, apiClientFactory } from '@doccareplus/api-client';
import type { User, AuthTokens } from '@doccareplus/kernel/domain';

/**
 * Login request payload
 */
export interface LoginRequest {
  email?: string;
  phoneNumber?: string;
  password?: string;
  otpCode?: string;
  twoFactorOtpCode?: string;
  deviceName?: string;
}

/**
 * Register request payload for default users
 */
export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

/**
 * Clinic Register request payload
 */
export interface ClinicRegisterRequest {
  phoneNumber: string;
  otpCode: string;
  password: string;
}

/**
 * Patient Register request payload
 */
export interface PatientRegisterRequest {
  email?: string;
  phoneNumber?: string;
  otpCode?: string;
  password?: string;
  fullName: string;
  dateOfBirth?: string;
}

/**
 * OTP delivery channel. Phone codes go over WhatsApp by default; SMS is not yet enabled.
 */
export type OtpChannel = 'WhatsApp' | 'Sms' | 'Email';

/**
 * Send OTP request payload. The recipient is the address matching the channel:
 * an E.164 phone for 'Sms', an email address for 'Email'.
 */
export interface SendOtpRequest {
  channel: OtpChannel;
  recipient: string;
  purpose: string;
}

/**
 * Verify a phone number for the authenticated account (compulsory secondary verification
 * for email-registered clinic owners).
 */
export interface VerifyPhoneRequest {
  phoneNumber: string;
  otpCode: string;
}

/**
 * Verify an email address for the authenticated account (optional secondary verification
 * for phone-registered clinic owners).
 */
export interface VerifyEmailRequest {
  email: string;
  otpCode: string;
}

/**
 * Reset password via phone OTP request payload
 */
export interface ResetPasswordWithOtpRequest {
  phoneNumber: string;
  otpCode: string;
  newPassword?: string;
}

/**
 * Clinic membership DTO
 */
export interface ClinicMembershipDto {
  clinicId: string;
  clinicName: string;
  role: string;
  roles?: string[];
  isOnboardingComplete: boolean;
  /** Clinic's ISO 3166-1 alpha-2 country code (from its address), if known. */
  country?: string;
  /** Clinic's city (from its address), if known. */
  city?: string;
  /** Clinic's billing currency (ISO code). Carried on the session so money surfaces never assume one. */
  currency?: string;
}

export interface UpdateCurrentUserProfileRequest {
  firstName: string;
  lastName: string;
  title?: string;
  middleName?: string;
}

/**
 * Raw Auth response from backend API
 */
export interface RawAuthResponse {
  userId: string;
  email: string;
  fullName: string;
  userType: string;
  role: string;
  accessToken: string;
  refreshToken: string;
  accessTokenExpiry: string;
  refreshTokenExpiry: string;
  memberships: ClinicMembershipDto[];
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
}

/**
 * Transformed Auth response used by Redux
 */
export interface AuthResponse {
  user: User;
  tokens: AuthTokens;
  memberships: ClinicMembershipDto[];
}

/**
 * Raw response from GET /auth/session — the authenticated user's profile plus their current clinic
 * memberships, re-queried live so selectable roles reflect server-side changes since login.
 */
export interface RawSessionResponse {
  user: unknown;
  memberships: ClinicMembershipDto[];
}

/**
 * Response from POST /auth/switch-context (Model B header-based auth).
 * Tokens are no longer returned; only the validated active context is.
 */
export interface SwitchContextResponse {
  clinicId: string;
  clinicName: string;
  role: string;
  roles: string[];
  workspaceProfile: import('@doccareplus/kernel/domain').WorkspaceProfile;
  defaultRoute: string;
}

/**
 * Helper to transform backend RawAuthResponse to frontend AuthResponse
 */
export function transformAuthResponse(raw: RawAuthResponse): AuthResponse {
  const nameParts = (raw.fullName || '').split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  const user: User = {
    id: raw.userId,
    email: raw.email,
    firstName,
    lastName,
    role: raw.role as User['role'],
    userType: raw.userType,
    isEmailVerified: raw.isEmailVerified ?? false,
    isPhoneVerified: raw.isPhoneVerified ?? false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const tokens: AuthTokens = {
    accessToken: raw.accessToken,
    refreshToken: raw.refreshToken,
    expiresAt: raw.accessTokenExpiry,
  };

  return {
    user,
    tokens,
    memberships: raw.memberships || [],
  };
}

/**
 * Auth API Service - Handles authentication endpoints
 */
class AuthApiService extends BaseApiService {
  constructor() {
    super('/auth');
  }

  /**
   * Login with email/phone and password/OTP
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const raw = await this.post<RawAuthResponse>('/login', credentials);
    return transformAuthResponse(raw);
  }

  /**
   * Register new user (standard)
   */
  async register(data: RegisterRequest): Promise<AuthResponse> {
    const raw = await this.post<RawAuthResponse>('/register', data);
    return transformAuthResponse(raw);
  }

  /**
   * Register new clinic and owner
   */
  async registerClinic(data: ClinicRegisterRequest): Promise<AuthResponse> {
    const raw = await this.post<RawAuthResponse>('/register/clinic', data);
    return transformAuthResponse(raw);
  }

  /**
   * Register new patient user
   */
  async registerPatient(data: PatientRegisterRequest): Promise<AuthResponse> {
    const raw = await this.post<RawAuthResponse>('/register/patient', data);
    return transformAuthResponse(raw);
  }

  /**
   * Send an OTP code over a channel (SMS or email) to the given recipient.
   */
  async sendOtp(data: SendOtpRequest): Promise<void> {
    return this.post<void>('/otp/send', data);
  }

  /**
   * Verify a phone number for the authenticated account (sets it confirmed).
   */
  async verifyPhone(data: VerifyPhoneRequest): Promise<void> {
    return this.post<void>('/verify/phone', data);
  }

  /**
   * Reset password using phone OTP code
   */
  async resetPasswordWithOtp(data: ResetPasswordWithOtpRequest): Promise<void> {
    return this.post<void>('/password-reset/otp', data);
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    return this.get<User>('/me');
  }

  async updateCurrentUserProfile(data: UpdateCurrentUserProfileRequest): Promise<void> {
    return this.put<void>('/profile', data);
  }

  /**
   * Fetch a fresh session snapshot (profile + current clinic memberships) without rotating tokens.
   * Called on app load to re-sync selectable roles changed server-side (e.g. a newly assigned staff role).
   */
  async getSession(): Promise<{ memberships: ClinicMembershipDto[] }> {
    const raw = await this.get<RawSessionResponse>('/session');
    return { memberships: raw.memberships ?? [] };
  }

  /**
   * Refresh access token
   */
  async refreshToken(accessToken: string, refreshToken: string): Promise<AuthResponse> {
    const raw = await this.post<RawAuthResponse>('/refresh-token', { accessToken, refreshToken });
    return transformAuthResponse(raw);
  }

  async switchContext(clinicId: string, role: string): Promise<SwitchContextResponse> {
    return this.post<SwitchContextResponse>('/switch-context', { clinicId, role });
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    return this.post<void>('/logout');
  }

  /**
   * Request password reset (email)
   */
  async forgotPassword(email: string): Promise<void> {
    return this.post<void>('/forgot-password', { email });
  }

  /**
   * Reset password with the token emailed by `forgotPassword`
   */
  async resetPassword(email: string, token: string, newPassword: string): Promise<void> {
    return this.post<void>('/reset-password', { email, token, newPassword });
  }

  /**
   * Verify an email address for the authenticated account (sets it confirmed, replacing the
   * synthetic placeholder for phone-registered owners).
   */
  async verifyEmail(data: VerifyEmailRequest): Promise<void> {
    return this.post<void>('/verify/email', data);
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return this.post<void>('/change-password', { currentPassword, newPassword });
  }
}

// Create singleton instance
export const authApi = new AuthApiService();

/**
 * Configure API client with auth token getters
 * Call this during app initialization
 */
export function configureAuthInterceptors(
  getToken: () => string | null,
  refreshToken: () => Promise<string | null>,
  onLogout: () => void
): void {
  apiClientFactory.setTokenGetter(getToken);
  apiClientFactory.setTokenRefresher(refreshToken);
  apiClientFactory.setLogoutCallback(onLogout);
}
